/**
 * Typed wrappers for the HR Core module (Phase 5.2) + the shared
 * employee directory. Paths and shapes are taken VERBATIM from
 * backend/internal/router/router.go and the onboarding.go /
 * hr_documents.go / assets.go / employees.go handlers.
 *
 * Cookie-only auth + the CSRF interceptor + 401→refresh are handled by
 * the shared axios instance (lib/api/client.ts). Every /hr route is
 * fenced server-side by RequireFeature("hr_core"); the api client turns
 * the backend's 403 { error: "upgrade_required" } into a /pricing
 * redirect, and other 403s reject normally (the caller renders a
 * graceful state).
 *
 * No client-supplied tenant_id is ever sent: the server derives it from
 * the session. The only identity a caller may pass is the legitimate
 * SUBJECT user_id (?user_id= on the document list, user_id in the
 * onboarding-assignment / asset-assign / document-upload bodies) — the
 * server authorizes the caller↔subject relationship and visibility
 * (models.CanViewDocument / canAccessInstance / GetUserByIDInTenant).
 *
 * SECURITY NOTES:
 *  - Document visibility is fully server-enforced. The list only
 *    returns docs the caller may see; a forbidden download is 404 (NOT
 *    403 — no existence oracle). There is NO client-side visibility
 *    logic anywhere. file_path (the storage key) is never on the wire.
 *  - The asset state machine is enforced server-side AND atomically in
 *    SQL; an illegal transition is 409 `illegal_transition`. The UI
 *    only hides controls that *would* 409 — the backend is the
 *    boundary.
 *  - Updating a template's task set 409s `template_has_instances` once
 *    it has live instances; the editor surfaces + handles this.
 */

import { api, blobErrorToApi } from "./client";
import type {
  AssignAssetRequest,
  AssignOnboardingRequest,
  Asset,
  AssetType,
  AssetTypeRequest,
  CreateAssetRequest,
  CreateEmployeeInput,
  EmployeeDTO,
  CreateEmployeeResponse,
  EmployeeAccountStatus,
  EmployeeProbation,
  LegacyProbationReview,
  ConfirmProbationResponse,
  CorrectEmailResponse,
  ResendInviteResponse,
  EmployeeOnboarding,
  HRDocument,
  HRDocumentResponse,
  ListAssetsResponse,
  ListAssetTypesResponse,
  ListEmployeeOnboardingResponse,
  ListEmployeesResponse,
  ListHRDocumentsResponse,
  ListOnboardingTemplatesResponse,
  OnboardingDetailResponse,
  OnboardingTemplate,
  TaskActionRequest,
  TemplateRequest,
} from "@/types/hr";

export const HR_BASE = "/api/v1/hr";
const EMPLOYEES_BASE = "/api/v1/employees";

// ---------------------------------------------------------------------------
// Employee directory (RBAC company_admin/hr_manager server-side)
// ---------------------------------------------------------------------------

// GET /employees — full tenant directory. RBAC'd: a non-HR caller 403s
// (the caller degrades to id-only labels). Used to resolve user_id ↔
// name and to populate the assign / upload-subject pickers.
export async function listEmployees(
  signal?: AbortSignal,
): Promise<ListEmployeesResponse> {
  const { data } = await api.get<ListEmployeesResponse>(EMPLOYEES_BASE, {
    signal,
  });
  return data;
}

// POST /employees — create an employee. The admin sets a temporary
// password AND the server emails a 7-day set-password invite (or an
// "added to company" note for an existing WorkRight login); the response
// carries `invite` saying which happened. authMW + csrfMW + RBAC company_admin|
// hr_manager + Audit server-side; the shared client injects X-CSRF-Token
// automatically. The body is sent VERBATIM as createEmployeeRequest —
// optional keys are simply absent (the server treats absent ⇒ unset),
// never empty strings. Errors propagate as the backend's
// { error, message, field } envelope:
//   409 email_taken (field email) · 400 weak_password (field password)
//   400 validation (field email|hire_date) · 400 invalid_role (field
//   role) · 403 role_not_grantable (field role) · 409 employee_code_taken
//   (field employee_id) · 400 invalid_payload. The caller wires `field`
//   to the form.
export async function createEmployee(
  body: CreateEmployeeInput,
): Promise<CreateEmployeeResponse> {
  const { data } = await api.post<CreateEmployeeResponse>(EMPLOYEES_BASE, body);
  return data;
}

// GET /employees/{id}/account-status — invite status + whether the viewing
// HR admin may deactivate this person directly (and why not).
export async function getEmployeeAccountStatus(
  id: number,
  signal?: AbortSignal,
): Promise<EmployeeAccountStatus> {
  const { data } = await api.get<EmployeeAccountStatus>(
    `${EMPLOYEES_BASE}/${id}/account-status`,
    { signal },
  );
  return data;
}

// POST /employees/{id}/resend-invite — re-send the set-password invite to
// someone who has never signed in. 409 already_signed_in /
// employee_inactive, 429 invite_recently_sent (10-minute cooldown),
// 503 email_not_configured, 502 invite_not_sent.
export async function resendEmployeeInvite(
  id: number,
): Promise<ResendInviteResponse> {
  const { data } = await api.post<ResendInviteResponse>(
    `${EMPLOYEES_BASE}/${id}/resend-invite`,
  );
  return data;
}

// GET /employees/{id}/probation — status chip data (HR only).
export async function getEmployeeProbation(
  id: number,
  signal?: AbortSignal,
): Promise<EmployeeProbation> {
  const { data } = await api.get<EmployeeProbation>(
    `${EMPLOYEES_BASE}/${id}/probation`,
    { signal },
  );
  return data;
}

// POST /employees/{id}/confirm-probation — stamps the confirmation and
// generates the probation-confirmation letter. 409 already_confirmed /
// no_probation / employee_inactive.
export async function confirmEmployeeProbation(
  id: number,
  body: { effective_date?: string; new_compensation_paise?: number } = {},
): Promise<ConfirmProbationResponse> {
  const { data } = await api.post<ConfirmProbationResponse>(
    `${EMPLOYEES_BASE}/${id}/confirm-probation`,
    body,
  );
  return data;
}

// GET /employees/probation-legacy — the pre-tracking probation
// confirmations HR reviews (HR only).
export async function getLegacyProbationReview(
  signal?: AbortSignal,
): Promise<LegacyProbationReview> {
  const { data } = await api.get<LegacyProbationReview>(
    `${EMPLOYEES_BASE}/probation-legacy`,
    { signal },
  );
  return data;
}

// POST /employees/probation-legacy/reviewed — close the review (idempotent).
export async function markLegacyProbationReviewed(): Promise<LegacyProbationReview> {
  const { data } = await api.post<LegacyProbationReview>(
    `${EMPLOYEES_BASE}/probation-legacy/reviewed`,
  );
  return data;
}

// POST /employees/{id}/probation/unconfirm — undo a pre-tracking
// confirmation for someone still awaiting it. 409 not_legacy_confirmation.
export async function unconfirmLegacyProbation(
  id: number,
): Promise<EmployeeProbation> {
  const { data } = await api.post<EmployeeProbation>(
    `${EMPLOYEES_BASE}/${id}/probation/unconfirm`,
  );
  return data;
}

// POST /employees/{id}/correct-email — fix a mistyped email for someone who
// has NEVER signed in. 409 already_signed_in / shared_login / email_taken /
// employee_inactive (each with the reason in `message`).
export async function correctEmployeeEmail(
  id: number,
  email: string,
): Promise<CorrectEmailResponse> {
  const { data } = await api.post<CorrectEmailResponse>(
    `${EMPLOYEES_BASE}/${id}/correct-email`,
    { email },
  );
  return data;
}

// PATCH /employees/{id} — RBAC + CSRF + Audit. Every field optional;
// only present fields are written (server uses nil-pointer "absent ⇒
// don't touch"). G4.8 added the 3 F&F fields; G5.4 added the 6 payroll
// fields (salary_special_allowance_paise / pf_applicable /
// bank_account_number / bank_ifsc / pan / tax_regime).
//
// Errors:
//   - 422 validation (field name varies — salary_*_paise, work_start_date,
//                     bank_ifsc, pan, tax_regime, manager_id) — caller
//                     wires the `field` to react-hook-form via
//                     handleAuthError.
//   - 404 not_found  — employee not in tenant
//   - 400 invalid_role / invalid_manager
//   - 422 manager_cycle (transitive cycle through manager_id)
// POST /api/v1/employees/delete-mistaken — "Delete — added by mistake"
// (prospect drop-off review 2026-10). dry_run answers who can / can't be
// deleted without changing anything. Per-person outcome, always 200.
export async function deleteMistakenEmployees(
  employeeIds: number[],
  dryRun: boolean,
): Promise<import("@/types/hr").DeleteMistakenResponse> {
  const { data } = await api.post<import("@/types/hr").DeleteMistakenResponse>(
    `${EMPLOYEES_BASE}/delete-mistaken`,
    { employee_ids: employeeIds, dry_run: dryRun },
  );
  return data;
}

export async function updateEmployee(
  id: number,
  body: import("@/types/hr").UpdateEmployeeInput,
): Promise<EmployeeDTO> {
  const { data } = await api.patch<EmployeeDTO>(
    `${EMPLOYEES_BASE}/${id}`,
    body,
  );
  return data;
}

// GET /employees/{id} is NOT a backend route (the directory `/employees`
// returns the full list with all fields including the G5.4 salary
// inputs). To "load one" for the edit form, the caller filters the list
// client-side — saves an extra round trip when the HR admin already
// loaded the list view.

// ---------------------------------------------------------------------------
// Onboarding — templates (mutations RBAC company_admin/hr_manager)
// ---------------------------------------------------------------------------

// GET /hr/onboarding/templates — non-HR sees active only; HR sees all
// (server-side, tasks omitted in the list projection).
export async function listOnboardingTemplates(
  signal?: AbortSignal,
): Promise<ListOnboardingTemplatesResponse> {
  const { data } = await api.get<ListOnboardingTemplatesResponse>(
    `${HR_BASE}/onboarding/templates`,
    { signal },
  );
  return data;
}

// GET /hr/onboarding/templates/{id} — single template WITH its tasks.
export async function getOnboardingTemplate(
  id: number,
  signal?: AbortSignal,
): Promise<OnboardingTemplate> {
  const { data } = await api.get<OnboardingTemplate>(
    `${HR_BASE}/onboarding/templates/${id}`,
    { signal },
  );
  return data;
}

// POST /hr/onboarding/templates — RBAC + CSRF + Audit. Returns the
// created template WITH tasks.
export async function createOnboardingTemplate(
  body: TemplateRequest,
): Promise<OnboardingTemplate> {
  const { data } = await api.post<OnboardingTemplate>(
    `${HR_BASE}/onboarding/templates`,
    body,
  );
  return data;
}

// DELETE /hr/onboarding/templates/{id} — RBAC + CSRF + Audit. This is a
// soft DEACTIVATE server-side (DeactivateOnboardingTemplate), not a row
// delete — live instances are preserved.
export async function deactivateOnboardingTemplate(
  id: number,
): Promise<void> {
  await api.delete(`${HR_BASE}/onboarding/templates/${id}`);
}

// ---------------------------------------------------------------------------
// Onboarding — assignments + instances (role-FILTERED in SQL)
// ---------------------------------------------------------------------------

// POST /hr/onboarding/assignments — RBAC + CSRF + Audit. Assigns a
// template to a SUBJECT employee (server resolves both in-tenant).
export async function assignOnboarding(
  body: AssignOnboardingRequest,
): Promise<EmployeeOnboarding> {
  const { data } = await api.post<EmployeeOnboarding>(
    `${HR_BASE}/onboarding/assignments`,
    body,
  );
  return data;
}

// GET /hr/onboarding[?status=] — employee → own; manager → own +
// direct reports; HR → tenant-all. The SQL scope is the real boundary;
// the status filter never widens it.
export async function listEmployeeOnboarding(
  params?: { status?: string },
  signal?: AbortSignal,
): Promise<ListEmployeeOnboardingResponse> {
  const { data } = await api.get<ListEmployeeOnboardingResponse>(
    `${HR_BASE}/onboarding`,
    { params, signal },
  );
  return data;
}

// GET /hr/onboarding/{id} — instance + per-task statuses +
// percent_done. A non-authorized caller gets 404 (no existence oracle).
export async function getEmployeeOnboarding(
  id: number,
  signal?: AbortSignal,
): Promise<OnboardingDetailResponse> {
  const { data } = await api.get<OnboardingDetailResponse>(
    `${HR_BASE}/onboarding/${id}`,
    { signal },
  );
  return data;
}

// POST /hr/onboarding/{id}/tasks/{taskId}/complete — CSRF + Audit.
// Returns the recomputed instance + task list + percent_done.
export async function completeOnboardingTask(
  instanceId: number,
  taskId: number,
  body?: TaskActionRequest,
): Promise<OnboardingDetailResponse> {
  const { data } = await api.post<OnboardingDetailResponse>(
    `${HR_BASE}/onboarding/${instanceId}/tasks/${taskId}/complete`,
    body ?? {},
  );
  return data;
}

// POST /hr/onboarding/{id}/tasks/{taskId}/skip — CSRF + Audit.
export async function skipOnboardingTask(
  instanceId: number,
  taskId: number,
  body?: TaskActionRequest,
): Promise<OnboardingDetailResponse> {
  const { data } = await api.post<OnboardingDetailResponse>(
    `${HR_BASE}/onboarding/${instanceId}/tasks/${taskId}/skip`,
    body ?? {},
  );
  return data;
}

// POST /hr/onboarding/{id}/cancel — CSRF + Audit. Freezes the instance
// (status → cancelled); the checklist can no longer be changed. Returns
// the updated EmployeeOnboarding. An instance already terminal
// (completed/cancelled) 409s `not_cancellable`; an unknown / non-authz
// id 404s `not_found` (no existence oracle).
export async function cancelEmployeeOnboarding(
  instanceId: number,
): Promise<EmployeeOnboarding> {
  const { data } = await api.post<EmployeeOnboarding>(
    `${HR_BASE}/onboarding/${instanceId}/cancel`,
    {},
  );
  return data;
}

// ---------------------------------------------------------------------------
// HR documents
// ---------------------------------------------------------------------------

// GET /hr/documents[?user_id=] — defaults to the caller's own docs;
// user_id is the SUBJECT (HR/manager-of authorized server-side; an
// unrelated subject yields an empty list, no existence oracle). The
// list is ALREADY visibility-filtered server-side.
export async function listHRDocuments(
  params?: { user_id?: number },
  signal?: AbortSignal,
): Promise<ListHRDocumentsResponse> {
  const { data } = await api.get<ListHRDocumentsResponse>(
    `${HR_BASE}/documents`,
    { params, signal },
  );
  return data;
}

// POST /hr/documents — multipart upload. CSRF + Audit. The CSRF-aware
// shared client injects X-CSRF-Token; we let the browser set the
// multipart Content-Type (with boundary) by passing FormData and an
// undefined Content-Type so axios drops the JSON default.
export async function uploadHRDocument(input: {
  userId: number;
  file: File;
  docType: string;
  visibility: string;
  expiresAt?: string; // YYYY-MM-DD
}): Promise<HRDocument> {
  const fd = new FormData();
  fd.append("user_id", String(input.userId));
  fd.append("doc_type", input.docType);
  fd.append("visibility", input.visibility);
  if (input.expiresAt) fd.append("expires_at", input.expiresAt);
  fd.append("file", input.file);
  const { data } = await api.post<HRDocumentResponse>(
    `${HR_BASE}/documents`,
    fd,
    { headers: { "Content-Type": undefined } },
  );
  return data.document;
}

// GET /hr/documents/{id}/download — authed blob stream. A doc the
// caller may not see is 404 (NOT 403); the caller surfaces that as
// "not available" — there is NO client-side visibility logic and the
// URL is never predictable/public (the bytes only exist behind this
// authed endpoint).
export async function downloadHRDocument(id: number): Promise<Blob> {
  try {
    const { data } = await api.get<Blob>(
      `${HR_BASE}/documents/${id}/download`,
      { responseType: "blob" },
    );
    return data;
  } catch (err) {
    // A blob-response error carries the JSON envelope as an UNPARSED
    // Blob; rehydrate it in place so the caller's toApiError() / the
    // not_found → "not available" branch works (no existence oracle —
    // the backend already 404s a forbidden/missing doc neutrally).
    throw await blobErrorToApi(err);
  }
}

// POST /hr/documents/{id}/sign — CSRF + Audit. HR (any tenant doc) or
// the subject on a doc they can see; nobody else (404, no oracle).
export async function signHRDocument(id: number): Promise<HRDocument> {
  const { data } = await api.post<HRDocumentResponse>(
    `${HR_BASE}/documents/${id}/sign`,
    {},
  );
  return data.document;
}

// DELETE /hr/documents/{id} — RBAC (HR-admin) + CSRF + Audit. The row
// is removed then the blob (orphan-tolerant ordering server-side).
export async function deleteHRDocument(id: number): Promise<void> {
  await api.delete(`${HR_BASE}/documents/${id}`);
}

// ---------------------------------------------------------------------------
// Assets + asset types (mutations RBAC company_admin/hr_manager)
// ---------------------------------------------------------------------------

// GET /hr/asset-types — any tenant user behind the feature gate.
export async function listAssetTypes(
  signal?: AbortSignal,
): Promise<ListAssetTypesResponse> {
  const { data } = await api.get<ListAssetTypesResponse>(
    `${HR_BASE}/asset-types`,
    { signal },
  );
  return data;
}

// POST /hr/asset-types — RBAC + CSRF + Audit.
export async function createAssetType(
  body: AssetTypeRequest,
): Promise<AssetType> {
  const { data } = await api.post<AssetType>(
    `${HR_BASE}/asset-types`,
    body,
  );
  return data;
}

// PATCH /hr/asset-types/{id} — RBAC + CSRF + Audit.
export async function updateAssetType(
  id: number,
  body: AssetTypeRequest,
): Promise<AssetType> {
  const { data } = await api.patch<AssetType>(
    `${HR_BASE}/asset-types/${id}`,
    body,
  );
  return data;
}

// GET /hr/assets[?status=&assigned_to=] — any tenant user behind the
// feature gate. Both filters are server-validated.
export async function listAssets(
  params?: { status?: string; assigned_to?: number },
  signal?: AbortSignal,
): Promise<ListAssetsResponse> {
  const { data } = await api.get<ListAssetsResponse>(
    `${HR_BASE}/assets`,
    { params, signal },
  );
  return data;
}

// GET /hr/assets/export.csv — HR-admin only (the projection carries
// holder emails/names + purchase prices). Fetched as a Blob so the
// caller hands it to the shared downloadBlob helper; blobErrorToApi
// rehydrates the JSON envelope on failure (same pattern as the
// bulk-import error-log download).
export async function exportAssetsCSV(): Promise<Blob> {
  try {
    const { data } = await api.get<Blob>(`${HR_BASE}/assets/export.csv`, {
      responseType: "blob",
    });
    return data;
  } catch (err) {
    throw await blobErrorToApi(err);
  }
}

// POST /hr/assets — RBAC + CSRF + Audit. New assets start `available`.
export async function createAsset(
  body: CreateAssetRequest,
): Promise<Asset> {
  const { data } = await api.post<Asset>(`${HR_BASE}/assets`, body);
  return data;
}

// POST /hr/assets/{id}/assign — RBAC + CSRF + Audit. available→assigned
// only (else 409 `illegal_transition`).
export async function assignAsset(
  id: number,
  body: AssignAssetRequest,
): Promise<Asset> {
  const { data } = await api.post<Asset>(
    `${HR_BASE}/assets/${id}/assign`,
    body,
  );
  return data;
}

// POST /hr/assets/{id}/return — RBAC + CSRF + Audit. assigned→available.
// Optional condition = the state the asset came back in (ledger A2).
export async function returnAsset(
  id: number,
  condition?: string,
): Promise<Asset> {
  const { data } = await api.post<Asset>(
    `${HR_BASE}/assets/${id}/return`,
    condition ? { condition } : {},
  );
  return data;
}

// ---------------------------------------------------------------------------
// Asset allocation ledger (migration 095 — slices A1/A2)
// ---------------------------------------------------------------------------

// One of MY open assignments (GET /me/assets).
export interface MyAsset {
  assignment_id: number;
  asset_id: number;
  asset_type_name: string;
  asset_tag?: string;
  serial_number?: string;
  assigned_at: string;
  condition_out?: string;
  acknowledged_at?: string;
}

// GET /me/assets — the CALLER's open assignments (self-scoped in SQL).
export async function listMyAssets(
  signal?: AbortSignal,
): Promise<{ assets: MyAsset[] }> {
  const { data } = await api.get<{ assets: MyAsset[] }>("/api/v1/me/assets", {
    signal,
  });
  return data;
}

// POST /me/assets/{assignmentId}/acknowledge — receipt sign-off, once,
// by the assignee only (CSRF + audit).
export async function acknowledgeMyAsset(
  assignmentId: number,
): Promise<{ acknowledged_at: string }> {
  const { data } = await api.post<{ acknowledged_at: string }>(
    `/api/v1/me/assets/${assignmentId}/acknowledge`,
  );
  return data;
}

// One custody-trail entry (GET /hr/assets/{id}/history, HR-admin).
export interface AssetHistoryEntry {
  id: number;
  user_id: number;
  user_name: string;
  assigned_at: string;
  assigned_by_name?: string;
  condition_out?: string;
  acknowledged_at?: string;
  returned_at?: string;
  closed_reason?: string;
  condition_in?: string;
}

// GET /hr/assets/{id}/history — full custody trail, HR-admin only.
export async function listAssetHistory(
  id: number,
  signal?: AbortSignal,
): Promise<{ history: AssetHistoryEntry[] }> {
  const { data } = await api.get<{ history: AssetHistoryEntry[] }>(
    `${HR_BASE}/assets/${id}/history`,
    { signal },
  );
  return data;
}

// POST /hr/assets/{id}/retire — RBAC + CSRF + Audit. (available|
// assigned)→retired (terminal).
export async function retireAsset(id: number): Promise<Asset> {
  const { data } = await api.post<Asset>(
    `${HR_BASE}/assets/${id}/retire`,
    {},
  );
  return data;
}

// POST /hr/assets/{id}/lost — RBAC + CSRF + Audit. (available|
// assigned)→lost (terminal).
export async function markAssetLost(id: number): Promise<Asset> {
  const { data } = await api.post<Asset>(
    `${HR_BASE}/assets/${id}/lost`,
    {},
  );
  return data;
}

// GET /employees/export.csv — HR headcount sheet (bank a/c masked,
// audit-logged server-side). status=all includes inactive employees;
// format=import limits the columns to the bulk-import template's.
export async function exportEmployeesCSV(opts?: {
  status?: "active" | "all";
  format?: "import" | "full";
}): Promise<Blob> {
  try {
    const { data } = await api.get<Blob>("/api/v1/employees/export.csv", {
      params: opts,
      responseType: "blob",
    });
    return data;
  } catch (err) {
    throw await blobErrorToApi(err);
  }
}
